import {
  distributionCentre,
  resolveScheduled,
  type Project,
  type RateOrInherit,
  type Scheduled,
} from '@finsim/model';

/** Annual rate a deterministic run uses for one spec at month t (inherit → `inherited[t]`). */
export function rateAt(
  spec: RateOrInherit,
  t: number,
  cpi: ArrayLike<number>,
  inherited: ArrayLike<number>,
): number {
  switch (spec.kind) {
    case 'fixed':
      return spec.rate;
    case 'cpiRelative':
      return (cpi[t] ?? 0) + spec.offset;
    case 'distribution':
      return distributionCentre(spec);
    case 'inherit':
      return inherited[t] ?? 0;
  }
}

/** Per-month annual rates of a scheduled rate (deterministic, like the engine). */
export function ratePath(
  s: Scheduled<RateOrInherit>,
  months: number,
  cpi: ArrayLike<number>,
  inherited: ArrayLike<number> = cpi,
): Float64Array {
  const { values } = resolveScheduled(s, months);
  return Float64Array.from(values, (spec, t) => rateAt(spec, t, cpi, inherited));
}

/** The global CPI path of a project (deterministic). */
export function cpiPath(project: Project): Float64Array {
  const months = project.assumptions.horizonMonths;
  const empty = new Float64Array(months);
  return ratePath(project.assumptions.cpi, months, empty, empty);
}

/** A category's default inflation path (inherit → global CPI). */
export function categoryInflationPath(
  project: Project,
  categoryId: string | undefined,
): Float64Array {
  const cpi = cpiPath(project);
  const category = project.categories.find((c) => c.id === categoryId);
  return category?.inflation ? ratePath(category.inflation, cpi.length, cpi, cpi) : cpi;
}

/** Numeric path of any schedule, for previews. */
export function valuePath<T>(
  s: Scheduled<T>,
  months: number,
  toNumber: (value: T, t: number) => number,
): Float64Array {
  const { values } = resolveScheduled(s, months);
  return Float64Array.from(values, (v, t) => toNumber(v, t));
}
